import React, { useRef, useState } from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { useHistory } from "react-router-dom";
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { BrCalendar } from './BrCalendar';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ErrorValidationMessage } from './ErrorValidationMessage';
import api from '../api/api';
import '../layout/sass/_form.scss';
import { InputTextarea } from 'primereact/inputtextarea';

export const FormPerfisMensagens = ({ isLoading, defaultValues, visible, setVisible }) => {

    const history = useHistory();
    const toast = useRef(null);
    const [isSubmiting, setIsSubmiting] = useState(false);

    const schema = Yup.object().shape({
        nome: Yup
            .string()
            .required("O nome é obrigatório")
            .max(200, "O nome dete ter no máximo 200 caracteres"),
        cpf: Yup
            .string()
            .required("O CPF é obrigatório"),
        email: Yup
            .string()
            .max(198, "O e-mail deve ter no máximo 198 caracteres")
            .required("O e-mail é obrigatório")
            .email("O e-mail inserido é inválido"),
        dataNascimento: Yup
            .date()
            .max(new Date(), "Data de nascimento inválida")
            .typeError("Uma data deve ser inserida")
            .required("A data de nascimento é obrigatória"),
        celular: Yup
            .string()
            .required("O celular é obrigatório"),
    });

    const formik = useFormik({
        initialValues: defaultValues,
        validationSchema: schema,
        enableReinitialize: true,
        onSubmit: async (data) => {
            setIsSubmiting(true);
            try {
                // O objeto 'data' já contém deficiencia e tipo nos formatos corretos do Enum
                await api.post("perfil", data);

                formik.resetForm();
                toast.current.show({ life: 2000, severity: 'success', summary: 'Cadastro concluído!', detail: `${data.nome} adicionado(a) com sucesso` });
                setTimeout(() => {
                    setVisible(false);
                }, 2000);
            }
            catch (error) {
                toast.current.show({ life: 2000, severity: 'error', summary: 'Erro ao cadastrar perfil!', detail: error.toString() });
            }
            setIsSubmiting(false);
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

    return (
        <>
            <Toast ref={toast} position="bottom-right" />
            <Dialog
                className='p-scrollpanel-bar-y'
                header='Adicionar Perfil'
                onHide={() => { setVisible(false); formik.resetForm(); }}
                visible={visible}
                breakpoints={{ '960px': '75vw', '640px': '100vw' }}
                style={{ width: '60vw' }}
            >
                <div className="card p-grid p-col-12 p-mx-0 p-mt-0">
                    <div className="p-col-12">
                        <form onSubmit={formik.handleSubmit}>
                            <div className="p-formgrid p-grid p-fluid p-mx-0">
                                {/* Nome */}
                                <div className="p-field p-col-12 p-sm-6">
                                    <label htmlFor="nome" className={classNames({ 'p-error': isFormFieldValid('nome') })}>Nome</label>
                                    {!isLoading ? <InputText id="nome" name="nome" value={formik.values.nome} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('nome') })} /> : <Skeleton height="35px" />}
                                    {formik.errors.nome && formik.touched.nome && <ErrorValidationMessage message={formik.errors.nome} />}
                                </div>

                                {/* CPF */}
                                <div className="p-field p-col-12 p-sm-3">
                                    <label htmlFor="cpf" className={classNames({ 'p-error': isFormFieldValid('cpf') })}>CPF</label>
                                    {!isLoading ? <InputMask id="cpf" name="cpf" mask="999.999.999-99" unmask={true} value={formik.values.cpf} onChange={formik.handleChange} className={classNames({ 'p-invalid': isFormFieldValid('cpf') })} /> : <Skeleton height="35px" />}
                                    {formik.errors.cpf && formik.touched.cpf && <ErrorValidationMessage message={formik.errors.cpf} />}
                                </div>

                                {/* Email */}
                                <div className="p-field p-col-12 p-sm-3">
                                    <label htmlFor="email" className={classNames({ 'p-error': isFormFieldValid('email') })}>E-mail</label>
                                    {!isLoading ? <InputText id="email" name="email" value={formik.values.email} readOnly /> : <Skeleton height="35px" />}
                                </div>

                                {/* Data Nascimento */}
                                <div className="p-field p-col-12 p-sm-4">
                                    <label htmlFor="dataNascimento" className={classNames({ 'p-error': isFormFieldValid('dataNascimento') })}>Data de nascimento</label>
                                    <BrCalendar id="dataNascimento" name="dataNascimento" value={formik.values.dataNascimento} onChange={formik.handleChange} yearRange={`1922:${new Date().getFullYear()}`} />
                                    {formik.errors.dataNascimento && formik.touched.dataNascimento && <ErrorValidationMessage message={formik.errors.dataNascimento} />}
                                </div>

                                {/* Celular */}
                                <div className="p-field p-col-12 p-sm-4">
                                    <label htmlFor="celular" className={classNames({ 'p-error': isFormFieldValid('celular') })}>Celular</label>
                                    {!isLoading ? <InputMask id="celular" name="celular" unmask={true} value={formik.values.celular} mask="(99) 99999-9999" onChange={formik.handleChange} /> : <Skeleton height="35px" />}
                                </div>

                                {/* Tipo de Perfil */}
                                <div className="p-field p-col-12 p-sm-4">
                                    <label htmlFor="tipo">Tipo de Perfil</label>
                                    {!isLoading ? <Dropdown id="tipo" name="tipo" value={formik.values.tipo} options={formik.values.tipoPerfis} onChange={(e) => formik.setFieldValue("tipo", e.value)} placeholder="Selecione o tipo" /> : <Skeleton height="35px" />}
                                </div>

                                {/* Deficiência (COM SIGLAS) */}
                                <div className="p-field p-col-12 p-sm-12">
                                    <label htmlFor="deficiencia">Deficiência / Transtorno</label>
                                    {!isLoading ? (
                                        <Dropdown
                                            id="deficiencia"
                                            name="deficiencia"
                                            value={formik.values.deficiencia}
                                            options={formik.values.deficienciaPerfis} // Recebe a lista de objetos do componente pai
                                            optionLabel="label"
                                            optionValue="value"
                                            filter
                                            onChange={(e) => formik.setFieldValue("deficiencia", e.value)}
                                            placeholder="Selecione a deficiência"
                                        />
                                    ) : (
                                        <Skeleton height="35px" />
                                    )}
                                </div>

                                {/* NOVO: Campo de Observações */}
                                <div className="p-field p-col-12">
                                    <label htmlFor="obs">Observações</label>
                                    {!isLoading ? (
                                        <InputTextarea
                                            id="obs"
                                            name="obs"
                                            value={formik.values.obs || ''}
                                            onChange={formik.handleChange}
                                            rows={3}
                                            autoResize
                                            placeholder="Detalhes adicionais..."
                                        />
                                    ) : (
                                        <Skeleton height="60px" />
                                    )}
                                </div>
                            </div>
                            
                            <div className="p-d-flex p-jc-end p-mt-3">
                                <Button label='Salvar Perfil' type="submit" icon="pi pi-check" loading={isSubmiting} style={{ width: 'auto' }} />
                            </div>
                        </form>
                    </div>
                </div>
            </Dialog>
        </>
    );
};